"use client";

import { c as _c } from "react/compiler-runtime";
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import { motion } from "motion/react";
import { createContext, useContext, useMemo, useRef } from "react";
import { useElementAttribute } from "../../hooks";
import { backgroundVariants, borderVariants, disabledVariants } from "../../styles";
import { classNames, tv } from "../../utils";
import { Flex } from "../Flex";
import { NamespacedLayoutGroup } from "../NamespacedLayoutGroup";

// Only supports single, horizontal select for now

const ToggleButtonGroupContext = createContext({
  variant: "primary",
  size: "md"
});
const toggleButtonGroupVariants = tv({
  base: "inline-flex rounded-md",
  variants: {
    variant: {
      primary: "gap-1 overflow-hidden bg-bg-additional-1 p-0.5",
      secondary: "",
      "secondary-transparent": "",
      "secondary-frosted": ""
    },
    size: {
      sm: "",
      md: ""
    }
  }
});
const toggleButtonGroupItemVariants = tv({
  base: classNames("relative flex items-center rounded-md py-1 text-text-secondary outline-hidden", "hover:text-text-primary data-[state=on]:font-medium data-[state=on]:text-text-primary", "active:scale-[0.97] transition-[scale,colors] duration-200 ease-out"),
  variants: {
    variant: {
      primary: "focus-visible:text-text-primary",
      secondary: "",
      "secondary-transparent": "",
      "secondary-frosted": ""
    },
    onlyIcon: {
      false: "",
      true: "aspect-square justify-center"
    },
    buttonGroupVariant: {
      primary: "",
      secondary: "",
      "secondary-transparent": "",
      "secondary-frosted": ""
    },
    size: {
      sm: "text-xs",
      md: "text-sm"
    }
  },
  compoundVariants: [{
    onlyIcon: false,
    size: "sm",
    class: "px-1.5"
  }, {
    onlyIcon: false,
    size: "md",
    class: "px-3"
  }, {
    buttonGroupVariant: "primary",
    size: "sm",
    class: "h-5"
  }, {
    buttonGroupVariant: "primary",
    size: "md",
    class: "h-7"
  }, {
    buttonGroupVariant: ["secondary", "secondary-transparent", "secondary-frosted"],
    size: "sm",
    class: "h-6"
  }, {
    buttonGroupVariant: ["secondary", "secondary-transparent", "secondary-frosted"],
    size: "md",
    class: "h-8"
  }]
});
const toggleButtonGroupItemBackgroundVariants = tv({
  base: "absolute inset-0 size-full rounded-md",
  variants: {
    variant: {
      primary: "bg-bg-primary",
      secondary: classNames(backgroundVariants({
        background: "primary"
      }), borderVariants({
        border: "border-1"
      })),
      "secondary-transparent": classNames(backgroundVariants({
        background: "primary",
        transparent: true
      }), borderVariants({
        border: "border-1",
        transparent: true
      })),
      "secondary-frosted": classNames(backgroundVariants({
        background: "frosted"
      }), borderVariants({
        border: "frosted"
      }))
    }
  }
});
function ToggleButtonGroup(t0) {
  const $ = _c(30);
  let children;
  let className;
  let defaultValue;
  let onValueChange;
  let props;
  let t1;
  let t2;
  let value;
  if ($[0] !== t0) {
    ({
      children,
      className,
      defaultValue,
      value,
      onValueChange,
      variant: t1,
      size: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = defaultValue;
    $[4] = onValueChange;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
    $[8] = value;
  } else {
    children = $[1];
    className = $[2];
    defaultValue = $[3];
    onValueChange = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
    value = $[8];
  }
  const variant = t1 === undefined ? "primary" : t1;
  const size = t2 === undefined ? "md" : t2;
  let t3;
  let t4;
  if ($[9] !== size || $[10] !== variant) {
    t4 = {
      variant,
      size
    };
    $[9] = size;
    $[10] = variant;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  t3 = t4;
  const contextValue = t3;
  let t5;
  if ($[12] !== onValueChange) {
    t5 = value_0 => {
      if (value_0) {
        onValueChange?.(value_0);
      }
    };
    $[12] = onValueChange;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== className || $[15] !== size || $[16] !== variant) {
    t6 = classNames(toggleButtonGroupVariants({
      variant,
      size
    }), className);
    $[14] = className;
    $[15] = size;
    $[16] = variant;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== children || $[19] !== t6) {
    t7 = <Flex className={t6}>{children}</Flex>;
    $[18] = children;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== defaultValue || $[22] !== props || $[23] !== t5 || $[24] !== t7 || $[25] !== value) {
    t8 = <ToggleGroupPrimitive.Root asChild={true} defaultValue={defaultValue} onValueChange={t5} type="single" value={value} {...props}>{t7}</ToggleGroupPrimitive.Root>;
    $[21] = defaultValue;
    $[22] = props;
    $[23] = t5;
    $[24] = t7;
    $[25] = value;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== contextValue || $[28] !== t8) {
    t9 = <NamespacedLayoutGroup><ToggleButtonGroupContext.Provider value={contextValue}>{t8}</ToggleButtonGroupContext.Provider></NamespacedLayoutGroup>;
    $[27] = contextValue;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
}
export const ToggleButtonGroupItem = t0 => {
  const $ = _c(28);
  let IconComponent;
  let children;
  let className;
  let disabled;
  let props;
  let value;
  if ($[0] !== t0) {
    ({
      className,
      disabled,
      children,
      icon: IconComponent,
      value,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = IconComponent;
    $[2] = children;
    $[3] = className;
    $[4] = disabled;
    $[5] = props;
    $[6] = value;
  } else {
    IconComponent = $[1];
    children = $[2];
    className = $[3];
    disabled = $[4];
    props = $[5];
    value = $[6];
  }
  const ref = useRef(null);
  const dataState = useElementAttribute(ref, "data-state");
  const {
    variant,
    size
  } = useContext(ToggleButtonGroupContext);
  const t1 = Boolean(IconComponent) && !children;
  let t2;
  if ($[7] !== className || $[8] !== disabled || $[9] !== size || $[10] !== t1 || $[11] !== variant) {
    t2 = classNames(toggleButtonGroupItemVariants({
      variant,
      buttonGroupVariant: variant,
      onlyIcon: t1,
      size
    }), disabledVariants({
      disabled
    }), className);
    $[7] = className;
    $[8] = disabled;
    $[9] = size;
    $[10] = t1;
    $[11] = variant;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== IconComponent || $[14] !== size) {
    t3 = IconComponent ? typeof IconComponent === "function" ? <IconComponent size={size === "sm" ? 16 : 20} /> : IconComponent : null;
    $[13] = IconComponent;
    $[14] = size;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== children || $[17] !== t3) {
    t4 = <span className="relative z-[1]">{t3}{children}</span>;
    $[16] = children;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== dataState || $[20] !== variant) {
    t5 = dataState === "on" && <motion.span className={classNames(toggleButtonGroupItemBackgroundVariants({
      variant
    }))} layoutId="item" transition={{
      type: "spring",
      bounce: 0,
      duration: 0.2
    }} />;
    $[19] = dataState;
    $[20] = variant;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== props || $[23] !== t2 || $[24] !== t4 || $[25] !== t5 || $[26] !== value) {
    t6 = <ToggleGroupPrimitive.Item className={t2} ref={ref} value={value} {...props}>{t4}{t5}</ToggleGroupPrimitive.Item>;
    $[22] = props;
    $[23] = t2;
    $[24] = t4;
    $[25] = t5;
    $[26] = value;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  return t6;
};

/**
 * A set of two-state buttons that can be toggled on or off.
 */
export { ToggleButtonGroup };